import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Paper,
  Tabs,
  Tab,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

const Login = () => {
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Registration form state
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    tenantName: '',
    tenantSlug: '',
    vicidialServer: {
      name: '',
      host: '',
      port: '3306',
      database: 'asterisk',
      username: '',
      password: '',
      apiUrl: '',
      apiKey: ''
    }
  });

  const { login, register, error, loading } = useAuth();
  const navigate = useNavigate();

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const success = await login(email, password);
    if (success) {
      navigate('/dashboard');
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    const success = await register(registerForm);
    if (success) {
      navigate('/dashboard');
    }
  };

  const handleRegisterChange = (field, value) => {
    if (field.startsWith('vicidialServer.')) {
      const serverField = field.replace('vicidialServer.', '');
      setRegisterForm(prev => ({
        ...prev,
        vicidialServer: {
          ...prev.vicidialServer,
          [serverField]: value
        }
      }));
    } else {
      setRegisterForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h4" sx={{ mb: 3 }}>
            🎯 Qualitron
          </Typography>
          
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
            <Tab label="Sign In" />
            <Tab label="Register" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          {tabValue === 0 && (
            <Box component="form" onSubmit={handleLoginSubmit} sx={{ width: '100%', maxWidth: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                Sign in to your account
              </Typography>
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </Box>
          )}

          {/* Registration Form */}
          {tabValue === 1 && (
            <Box component="form" onSubmit={handleRegisterSubmit} sx={{ width: '100%' }}>
              <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
                Create your Qualitron account
              </Typography>

              {/* User Information */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                👤 User Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Full Name"
                    value={registerForm.name}
                    onChange={(e) => handleRegisterChange('name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username (optional)"
                    value={registerForm.username}
                    onChange={(e) => handleRegisterChange('username', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => handleRegisterChange('email', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Password"
                    type="password"
                    value={registerForm.password}
                    onChange={(e) => handleRegisterChange('password', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={registerForm.confirmPassword}
                    onChange={(e) => handleRegisterChange('confirmPassword', e.target.value)}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Tenant Information */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                🏢 Organization Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    required
                    fullWidth
                    label="Organization Name"
                    value={registerForm.tenantName}
                    onChange={(e) => handleRegisterChange('tenantName', e.target.value)}
                    helperText="Your company or organization name"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Slug (optional)"
                    value={registerForm.tenantSlug}
                    onChange={(e) => handleRegisterChange('tenantSlug', e.target.value)}
                    helperText="URL-friendly identifier"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Vicidial Server Configuration */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    📞 Vicidial Server Configuration
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Server Name"
                        value={registerForm.vicidialServer.name}
                        onChange={(e) => handleRegisterChange('vicidialServer.name', e.target.value)}
                        helperText="A friendly name for your Vicidial server"
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        required
                        fullWidth
                        label="Host/IP Address"
                        value={registerForm.vicidialServer.host}
                        onChange={(e) => handleRegisterChange('vicidialServer.host', e.target.value)}
                        helperText="IP address or hostname of your Vicidial server"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Port"
                        value={registerForm.vicidialServer.port}
                        onChange={(e) => handleRegisterChange('vicidialServer.port', e.target.value)}
                        helperText="Database port (default: 3306)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Database Name"
                        value={registerForm.vicidialServer.database}
                        onChange={(e) => handleRegisterChange('vicidialServer.database', e.target.value)}
                        helperText="Database name (default: asterisk)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Database Username"
                        value={registerForm.vicidialServer.username}
                        onChange={(e) => handleRegisterChange('vicidialServer.username', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Database Password"
                        type="password"
                        value={registerForm.vicidialServer.password}
                        onChange={(e) => handleRegisterChange('vicidialServer.password', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="API URL (optional)"
                        value={registerForm.vicidialServer.apiUrl}
                        onChange={(e) => handleRegisterChange('vicidialServer.apiUrl', e.target.value)}
                        helperText="Vicidial API endpoint URL"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="API Key (optional)"
                        value={registerForm.vicidialServer.apiKey}
                        onChange={(e) => handleRegisterChange('vicidialServer.apiKey', e.target.value)}
                        helperText="API authentication key"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 4, mb: 2 }}
                disabled={loading}
                size="large"
              >
                {loading ? 'Creating Account...' : 'Create Account & Setup Organization'}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 